import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Mail, Eye, EyeOff } from "lucide-react";
import { verifyOTP, changePassword } from "@/utils/auth";

/**
 * OTP input dialog for 2-Factor Authentication step.
 * Props:
 *   - open, onOpenChange: dialog visibility
 *   - role: which role is logging in ("admin", "hr", etc.)
 *   - emailHint: masked email like "abc***@domain.com"
 *   - devOtp: OTP returned by backend in dev mode when SMTP is unavailable
 *   - onVerified: called with response data on successful OTP verification
 */
export const TwoFactorDialog = ({ open, onOpenChange, role, emailHint, devOtp, onVerified }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const data = await verifyOTP(role, otp.trim());
      toast.success("Verified! Logging you in...");
      onOpenChange(false);
      setOtp("");
      onVerified(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-amber-500/40 text-white max-w-md" data-testid="two-factor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            <ShieldCheck className="h-5 w-5" /> Two-Factor Authentication
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {emailHint ? (
              <>A 6-digit code was sent to <span className="text-amber-300 font-mono">{emailHint}</span>. Enter it below.</>
            ) : (
              <>Enter the 6-digit verification code.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleVerify} className="space-y-4 pt-2">
          {devOtp && (
            <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-md p-3 text-xs">
              <Mail className="h-4 w-4 inline mr-1 text-yellow-400" />
              <b className="text-yellow-300">Dev mode:</b> SMTP is not configured — your OTP is{" "}
              <code className="bg-gray-800 px-1.5 py-0.5 rounded font-mono text-yellow-200" data-testid="dev-otp-hint">
                {devOtp}
              </code>
            </div>
          )}
          <div>
            <Label className="text-gray-300">Verification Code</Label>
            <Input
              autoFocus
              data-testid="otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="bg-gray-800 border-gray-600 text-white text-center text-2xl tracking-[0.5em] font-mono mt-1"
              inputMode="numeric"
              maxLength={6}
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-gray-600 text-gray-300 hover:bg-gray-800">
              Cancel
            </Button>
            <Button type="submit" disabled={loading || otp.length < 4} className="bg-amber-600 hover:bg-amber-700" data-testid="verify-otp-btn">
              {loading ? "Verifying..." : "Verify"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Force-password-change dialog shown on first login when a user still has the default password.
 * Props:
 *   - open, onOpenChange: dialog visibility
 *   - role: role name
 *   - currentPassword: the password just used to login (pre-filled so user doesn't retype)
 *   - onChanged: called when password has been changed successfully
 */
export const ForcePasswordChangeDialog = ({ open, onOpenChange, role, currentPassword, onChanged }) => {
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPwd === currentPassword) {
      toast.error("New password must differ from the current one");
      return;
    }
    setLoading(true);
    try {
      await changePassword(role, currentPassword, newPwd);
      // Clear local form state first so unmount cleanup is clean.
      setNewPwd("");
      setConfirmPwd("");
      // Tell parent the dialog can close — it sets flow.forcePwd = null.
      onOpenChange(false);
      toast.success("Password updated — taking you in!");
      // Defer navigation by a microtask so Dialog's exit animation can start.
      setTimeout(() => onChanged(), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="bg-gray-900 border-red-500/40 text-white max-w-md [&>button]:hidden" data-testid="force-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-300">
            <KeyRound className="h-5 w-5" /> Set a new password
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            For your security, you must change the default password before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="relative">
            <Label className="text-gray-300">New password (min 8 characters)</Label>
            <Input
              autoFocus
              type={show ? "text" : "password"}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="bg-gray-800 border-gray-600 text-white mt-1 pr-10"
              data-testid="new-password-input"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-8 text-gray-400">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div>
            <Label className="text-gray-300">Confirm new password</Label>
            <Input
              type={show ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              className="bg-gray-800 border-gray-600 text-white mt-1"
              data-testid="confirm-password-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 w-full" data-testid="submit-new-password-btn">
              {loading ? "Updating..." : "Update Password & Continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
